import {urlFor} from '@ohrm/core/util/helper/url';

type BeaconMetadataField = {
  key: string;
  value: string;
};

type BeaconConfiguration = {
  enabled: boolean;
  includeAuthToken: boolean;
  includeMetadata: boolean;
  includeDomains: boolean;
  sdkUrl: string;
  orgId: string;
  branchId?: string;
  clientId?: string;
  uiConfigOverrides?: string;
  cdnUrl: string;
  apiUrl: string;
  metadata: BeaconMetadataField[];
  username?: string;
};

type BeaconTokenPayload = {
  accessToken: string;
  accessTokenExpiresAt?: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
};

type BeaconBar = {
  orgId: string;
  opened?: boolean;
  user?: Record<string, unknown>;
  open: () => void;
  logout?: () => void;
};

declare global {
  interface Window {
    BeaconBar?: BeaconBar;
  }
}

const PENDING_CONFIGURATION_KEY = 'beacon.pendingConfiguration';
const TOKEN_ENDPOINT = '/api/v2/auth/beacon/token';

export function clearBeaconRuntime(): void {
  if (window.BeaconBar?.logout) window.BeaconBar.logout();
  document
    .querySelectorAll(
      '#beacon-user, script[data-beacon-sdk="true"], script[src*="beacon"]',
    )
    .forEach((element) => {
      element.remove();
    });
  delete window.BeaconBar;
}

export function registerBeaconLogoutCleanup(): void {
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target as HTMLElement | null;
      const logoutLink = target?.closest('a[href*="/auth/logout"]');
      if (logoutLink) clearBeaconRuntime();
    },
    true,
  );
}

function getMetadata(
  configuration: BeaconConfiguration,
): Record<string, unknown> | undefined {
  if (!configuration.includeMetadata) return undefined;

  const metadata: Record<string, unknown> = {};
  configuration.metadata.forEach(({key, value}) => {
    const normalizedKey = key.trim();
    const normalizedValue = value.trim();
    if (!normalizedKey || !normalizedValue) return;
    if (['shardKey1', 'shardKey2', 'shardKey3'].includes(normalizedKey)) {
      metadata[normalizedKey] = normalizedValue
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      return;
    }
    metadata[normalizedKey] = normalizedValue;
  });

  return Object.keys(metadata).length > 0 ? metadata : undefined;
}

function showBeaconFailure(message: string): void {
  const banner = document.createElement('div');
  banner.setAttribute('role', 'alert');
  const messageText = document.createElement('span');
  messageText.textContent = `Beacon script was not loaded: ${message}. Check the Beacon dashboard settings.`;
  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.textContent = 'Close';
  closeButton.setAttribute('aria-label', 'Close Beacon warning');
  closeButton.addEventListener('click', () => banner.remove());
  banner.append(messageText, closeButton);
  Object.assign(banner.style, {
    position: 'fixed',
    top: '1rem',
    right: '1rem',
    zIndex: '9999',
    maxWidth: '28rem',
    padding: '0.75rem 1rem',
    background: '#fff3cd',
    border: '1px solid #ffca2c',
    color: '#664d03',
    fontFamily: 'sans-serif',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
  });
  Object.assign(closeButton.style, {
    border: '0',
    background: 'transparent',
    color: '#664d03',
    cursor: 'pointer',
    fontWeight: '700',
  });
  document.body.appendChild(banner);
}

function loadBeaconScript(
  configuration: BeaconConfiguration,
  token?: BeaconTokenPayload,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let sdkUrl: URL;
    try {
      sdkUrl = new URL(configuration.sdkUrl);
      if (sdkUrl.protocol !== 'https:')
        throw new Error('SDK URL must use HTTPS');
    } catch (error) {
      reject(error);
      return;
    }

    const metadata = getMetadata(configuration);
    const beaconUser: Record<string, unknown> = {
      identifier: configuration.username || 'authenticated-user',
    };
    if (configuration.clientId?.trim())
      beaconUser.clientId = configuration.clientId.trim();
    if (metadata) beaconUser.metaData = metadata;
    if (token) beaconUser.authToken = token;

    const beaconConfig: BeaconBar = {
      orgId: configuration.orgId,
      open: () => {
        if (window.BeaconBar) window.BeaconBar.opened = true;
      },
      user: beaconUser,
    };
    if (configuration.branchId?.trim()) {
      (beaconConfig as unknown as Record<string, unknown>).branchId =
        configuration.branchId.trim();
    }
    if (configuration.uiConfigOverrides?.trim()) {
      const overrides = JSON.parse(configuration.uiConfigOverrides);
      (beaconConfig as unknown as Record<string, unknown>).uiConfigOverrides =
        overrides;
    }
    if (configuration.includeDomains) {
      (beaconConfig as unknown as Record<string, unknown>).domains = {
        cdnUrl: configuration.cdnUrl,
        apiUrl: configuration.apiUrl,
      };
    }
    window.BeaconBar = beaconConfig;

    const container = document.createElement('div');
    container.id = 'beacon-user';
    container.dataset.extension = 'true';
    container.dataset.orgId = configuration.orgId;
    document.body.appendChild(container);

    const script = document.createElement('script');
    script.src = sdkUrl.toString();
    script.dataset.beaconSdk = 'true';
    script.async = true;
    script.type = 'module';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('SDK download failed'));
    document.head.appendChild(script);
  });
}

async function requestBeaconToken(
  username?: string,
): Promise<BeaconTokenPayload> {
  const response = await fetch(urlFor(TOKEN_ENDPOINT), {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    credentials: 'same-origin',
    body: JSON.stringify({username}),
  });
  if (!response.ok) throw new Error('Token retrieval failed');
  return response.json();
}

export async function initializeBeaconAfterLogin(): Promise<void> {
  if (window.location.pathname.endsWith('/auth/login')) {
    clearBeaconRuntime();
    window.localStorage.removeItem(PENDING_CONFIGURATION_KEY);
    return;
  }

  if (document.getElementById('beacon-user')) return;

  const serializedConfiguration = window.localStorage.getItem(
    PENDING_CONFIGURATION_KEY,
  );
  if (!serializedConfiguration) return;

  let configuration: BeaconConfiguration;
  try {
    configuration = JSON.parse(serializedConfiguration);
  } catch (error) {
    showBeaconFailure('saved configuration is invalid');
    return;
  }
  if (!configuration.enabled) return;

  try {
    const token = configuration.includeAuthToken
      ? await requestBeaconToken(configuration.username)
      : undefined;
    await loadBeaconScript(configuration, token);
  } catch (error) {
    showBeaconFailure(error instanceof Error ? error.message : 'unknown error');
  }
}
